import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchHighlightJumpPosition } from '@/api/services/highlight'
import { CbzManifest, fetchCbzManifest, fetchCbzPage, getSignedUrls } from '@/api/services/media'
import { getViewCachePages, saveViewCachePages } from '@/lib/db'
import { useProgressStore } from '@/store/useProgressStore'

import { useHighlightJumpId } from '../hooks/useHighlightJumpId'
import { ImageReader } from '../image/image-reader'
import { ReaderLoading } from '../shared/reader-loading'

/**
 * 画像の状態
 */
type ImageState =
  | { status: 'pending' } // 未取得
  | { status: 'loading'; abortController: AbortController } // 取得中
  | { status: 'loaded'; url: string; blob: Blob } // 取得済み（Object URL + Blob保持）
  | { status: 'error'; message: string; retryCount: number } // エラー（リトライ回数付き）

/** リトライ設定 */
const MAX_RETRY_COUNT = 3
const RETRY_DELAY_MS = 500

type Props = {
  url: string
  title: string
  onInfoClick?: () => void
  onNext?: () => void
  onPrev?: () => void
  onClose: () => void
  numPages?: number
  episodeId?: number
  episodeIndex?: number
  productId?: number
  isFavorite?: boolean
  onFavoriteToggle?: () => void
  onDelete?: () => void
}

/**
 * 優先度付きページインデックスを計算
 * 現在ページから前後に広がる順序でインデックスを返す
 */
const calculatePriorityIndices = (currentPage: number, totalPages: number, range: number = 2): number[] => {
  const indices: number[] = []
  const added = new Set<number>()

  // 現在ページを最優先
  if (currentPage >= 0 && currentPage < totalPages) {
    indices.push(currentPage)
    added.add(currentPage)
  }

  // 前後に広がる（±1, ±2, ...）
  for (let offset = 1; offset <= range; offset++) {
    const before = currentPage - offset
    const after = currentPage + offset

    if (after < totalPages && !added.has(after)) {
      indices.push(after)
      added.add(after)
    }
    if (before >= 0 && !added.has(before)) {
      indices.push(before)
      added.add(before)
    }
  }

  return indices
}

/**
 * 残りのページインデックスを計算（現在位置以降を先頭から → 現在位置より前を近い順）
 */
const calculateRemainingIndices = (
  currentPage: number,
  totalPages: number,
  loadedSet: Set<number>,
  loadingSet: Set<number>,
): number[] => {
  const isPending = (i: number) => !loadedSet.has(i) && !loadingSet.has(i)
  const after = Array.from({ length: Math.max(totalPages - currentPage, 0) }, (_, i) => currentPage + i)
  const before = Array.from({ length: Math.min(currentPage, totalPages) }, (_, i) => currentPage - 1 - i)
  return [...after, ...before].filter(isPending)
}

/** 進捗（1ベース）かジャンプ先のページ（0ベース）を 0 ベースの開始ページに変換し、範囲内に収める */
const toStartPage = (progress: number | undefined, totalPages: number, jumpPage?: number): number =>
  Math.min(Math.max(jumpPage ?? (progress ?? 0) - 1, 0), Math.max(totalPages - 1, 0))

export const ZipReader = (props: Props) => {
  const { episodeId, numPages: propsNumPages } = props

  // マニフェスト
  const [manifest, setManifest] = useState<CbzManifest | null>(null)
  const [manifestLoading, setManifestLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 画像状態管理
  const imageStatesRef = useRef<Map<number, ImageState>>(new Map())
  const [loadedCount, setLoadedCount] = useState(0)

  // 署名付きURL（ページごと）
  const signedUrlsRef = useRef<Map<number, string>>(new Map())

  // 現在のページ（ImageReaderから更新される）
  const [currentPage, setCurrentPage] = useState(0)
  // 進捗から求めた開始ページ（ImageReader の復元位置にも使う）
  const [startPage, setStartPage] = useState(0)

  // バックグラウンド読み込み中フラグ
  const isBackgroundLoadingRef = useRef(false)

  // 初回読み込み完了フラグ（重複防止）
  const initialLoadDoneRef = useRef(false)

  // キャッシュチェック完了フラグ
  const cacheCheckDoneRef = useRef(false)

  // キャッシュから復元したか
  const [restoredFromCache, setRestoredFromCache] = useState(false)
  // キャッシュから復元した際のページ数（imageStatesRef.current.size を render 中に読まないため）
  const [restoredSize, setRestoredSize] = useState(0)

  // クリーンアップ用
  const mountedRef = useRef(true)

  // 総ページ数（キャッシュ復元時は復元時のサイズを使用）
  const totalPages = manifest?.total ?? (restoredFromCache ? restoredSize : (propsNumPages ?? 0))

  // ?hl= があれば、進捗ではなくそのハイライトのページから開く
  const jumpHighlightId = useHighlightJumpId()

  // マニフェスト取得
  useEffect(() => {
    if (!episodeId) {
      setError('Episode ID is required')
      setManifestLoading(false)
      return
    }

    // キャッシュチェック → キャッシュがあれば復元、なければマニフェスト取得
    const init = async () => {
      const progressPromise = useProgressStore.getState().fetchEpisodeProgress(episodeId)
      const jumpPagePromise = fetchHighlightJumpPosition(jumpHighlightId, episodeId).then(
        (jump) => jump?.selections?.[0]?.page,
      )

      // キャッシュチェック（初回のみ）
      if (!cacheCheckDoneRef.current) {
        cacheCheckDoneRef.current = true
        try {
          const cachedPages = await getViewCachePages(episodeId)
          if (mountedRef.current && cachedPages.length > 0) {
            const [progress, jumpPage] = await Promise.all([progressPromise, jumpPagePromise])
            if (!mountedRef.current) return
            setStartPage(toStartPage(progress?.progress, cachedPages.length, jumpPage))
            // キャッシュから復元
            imageStatesRef.current.clear()
            for (let i = 0; i < cachedPages.length; i++) {
              const page = cachedPages[i]!
              const objectUrl = URL.createObjectURL(page.blob)
              imageStatesRef.current.set(page.pageIndex, { status: 'loaded', url: objectUrl, blob: page.blob })
            }
            setLoadedCount(cachedPages.length)
            setRestoredSize(cachedPages.length)
            setRestoredFromCache(true)
            initialLoadDoneRef.current = true
            setManifestLoading(false)
            return
          }
        } catch {
          // キャッシュ読み込み失敗 — 通常フローへ
        }
      }

      // 通常フロー: マニフェスト取得
      try {
        setManifestLoading(true)
        const [data, progress, jumpPage] = await Promise.all([
          fetchCbzManifest(episodeId),
          progressPromise,
          jumpPagePromise,
        ])
        if (mountedRef.current) {
          // 進捗のページから優先・バックグラウンド読み込みを始める
          const start = toStartPage(progress?.progress, data.total, jumpPage)
          setCurrentPage(start)
          setStartPage(start)
          setManifest(data)
          // 状態マップを初期化
          imageStatesRef.current.clear()
          for (let i = 0; i < data.total; i++) {
            imageStatesRef.current.set(i, { status: 'pending' })
          }
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'マニフェスト取得エラー')
        }
      } finally {
        if (mountedRef.current) {
          setManifestLoading(false)
        }
      }
    }

    init()

    return () => {
      mountedRef.current = false
    }
  }, [episodeId, jumpHighlightId])

  // 署名付きURLをバッチ取得
  const fetchSignedUrlsBatch = useCallback(
    async (indices: number[]) => {
      if (!episodeId) return

      // 未取得のインデックスのみ
      const needUrls = indices.filter((i) => !signedUrlsRef.current.has(i))
      if (needUrls.length === 0) return

      const paths = needUrls.map((i) => `/e/${episodeId}/cbz/page/${i}`)
      try {
        const urls = await getSignedUrls(paths)
        const urlsMap = new Map(Object.entries(urls))
        needUrls.forEach((pageIndex, idx) => {
          const path = paths.at(idx)
          if (!path) return
          const signedUrl = urlsMap.get(path)
          if (signedUrl) {
            signedUrlsRef.current.set(pageIndex, signedUrl)
          }
        })
      } catch (err) {
        console.error('[ZipReader] Failed to fetch signed URLs:', err)
      }
    },
    [episodeId],
  )

  // 単一ページを取得（リトライ付き）
  const fetchPage = useCallback(
    async (index: number) => {
      if (!episodeId) return
      if (!mountedRef.current) return

      const currentState = imageStatesRef.current.get(index)
      if (currentState?.status === 'loading' || currentState?.status === 'loaded') {
        return // 既に取得中または取得済み
      }

      const abortController = new AbortController()
      imageStatesRef.current.set(index, { status: 'loading', abortController })

      let retryCount = 0
      while (true) {
        if (!mountedRef.current || abortController.signal.aborted) {
          return
        }

        try {
          const signedUrl = signedUrlsRef.current.get(index)
          const blob = await fetchCbzPage(episodeId, index, signedUrl, abortController.signal)

          if (!mountedRef.current || abortController.signal.aborted) {
            return
          }

          const objectUrl = URL.createObjectURL(blob)
          imageStatesRef.current.set(index, { status: 'loaded', url: objectUrl, blob })
          setLoadedCount((prev) => prev + 1)
          return
        } catch (err) {
          if (!mountedRef.current || abortController.signal.aborted) {
            return
          }

          if (retryCount < MAX_RETRY_COUNT) {
            retryCount++
            console.warn(`[ZipReader] Retrying page ${index} (attempt ${retryCount}/${MAX_RETRY_COUNT})`)
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * retryCount))
            if (!mountedRef.current || abortController.signal.aborted) return
            continue
          }

          const message = err instanceof Error ? err.message : 'ページ取得エラー'
          imageStatesRef.current.set(index, { status: 'error', message, retryCount })
          console.error('[ZipReader] Failed to fetch page %d after %d retries:', index, MAX_RETRY_COUNT, err)
          return
        }
      }
    },
    [episodeId],
  )

  // 優先ページを取得（並列）
  const fetchPriorityPages = useCallback(
    async (indices: number[]) => {
      // 署名付きURLを先に取得
      await fetchSignedUrlsBatch(indices)

      // 並列でページ取得
      await Promise.all(indices.map((i) => fetchPage(i)))
    },
    [fetchSignedUrlsBatch, fetchPage],
  )

  // バックグラウンドで残りのページを順次取得
  const fetchRemainingPages = useCallback(async () => {
    if (isBackgroundLoadingRef.current) return
    isBackgroundLoadingRef.current = true

    const loadedSet = new Set<number>()
    const loadingSet = new Set<number>()

    imageStatesRef.current.forEach((state, index) => {
      if (state.status === 'loaded') loadedSet.add(index)
      if (state.status === 'loading') loadingSet.add(index)
    })

    const remaining = calculateRemainingIndices(currentPage, totalPages, loadedSet, loadingSet)

    // 5枚ずつバッチ処理
    const batchSize = 5
    for (let i = 0; i < remaining.length; i += batchSize) {
      if (!mountedRef.current) break

      const batch = remaining.slice(i, i + batchSize)
      await fetchSignedUrlsBatch(batch)
      await Promise.all(batch.map((idx) => fetchPage(idx)))

      // 少し待機してUIをブロックしない
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    isBackgroundLoadingRef.current = false
  }, [currentPage, totalPages, fetchSignedUrlsBatch, fetchPage])

  // マニフェスト取得後に初期ページを読み込み（初回のみ）
  useEffect(() => {
    if (!manifest || manifestLoading) return
    if (initialLoadDoneRef.current) return // 既に初期読み込み済み

    initialLoadDoneRef.current = true

    const loadInitialPages = async () => {
      // 優先ページ（現在 ± 2）を取得
      const priorityIndices = calculatePriorityIndices(currentPage, totalPages, 2)
      await fetchPriorityPages(priorityIndices)

      // バックグラウンドで残りを取得開始
      fetchRemainingPages()
    }

    loadInitialPages()
  }, [manifest, manifestLoading, currentPage, totalPages, fetchPriorityPages, fetchRemainingPages])

  // ページ移動時に優先度を更新
  const handlePageChange = useCallback(
    async (newPage: number) => {
      setCurrentPage(newPage)

      // 新しい優先ページが未取得なら取得
      const priorityIndices = calculatePriorityIndices(newPage, totalPages, 2)
      const needFetch = priorityIndices.filter((i) => {
        const state = imageStatesRef.current.get(i)
        return !state || state.status === 'pending'
      })

      if (needFetch.length > 0) {
        await fetchPriorityPages(needFetch)
      }
    },
    [totalPages, fetchPriorityPages],
  )

  // 画像配列（ImageReaderに渡す形式）
  const [images, setImages] = useState<(string | null)[]>([])

  // loadedCount または totalPages が変わったら画像配列を更新
  useEffect(() => {
    const newImages = Array.from({ length: totalPages }, (_, i) => {
      const state = imageStatesRef.current.get(i)
      return state?.status === 'loaded' ? state.url : null
    })
    setImages(newImages)
  }, [totalPages, loadedCount])

  // ストリーム完了判定
  const isStreamComplete = loadedCount >= totalPages

  // 全ページ読み込み完了時にキャッシュ保存（キャッシュ復元でない場合のみ）
  useEffect(() => {
    if (!isStreamComplete || restoredFromCache || !episodeId || totalPages === 0) return

    const pages: { index: number; blob: Blob }[] = []
    imageStatesRef.current.forEach((state, index) => {
      if (state.status === 'loaded') {
        pages.push({ index, blob: state.blob })
      }
    })

    if (pages.length === totalPages) {
      saveViewCachePages(episodeId, pages).catch(console.error)
    }
  }, [isStreamComplete, restoredFromCache, episodeId, totalPages])

  // クリーンアップ: Object URLを解放
  useEffect(() => {
    const currentImageStates = imageStatesRef.current
    return () => {
      mountedRef.current = false
      currentImageStates.forEach((state) => {
        if (state.status === 'loaded') {
          URL.revokeObjectURL(state.url)
        }
        if (state.status === 'loading') {
          state.abortController.abort()
        }
      })
      currentImageStates.clear()
    }
  }, [])

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-red-500">エラー: {error}</div>
      </div>
    )
  }

  if (manifestLoading) {
    return <ReaderLoading message="マニフェスト読み込み中..." />
  }

  if (loadedCount === 0) {
    return <ReaderLoading message="ページ読み込み中..." />
  }

  return (
    <ImageReader
      {...props}
      images={images}
      loadedCount={loadedCount}
      totalCount={totalPages}
      isStreamComplete={isStreamComplete}
      onPageChange={handlePageChange}
      initialPageIndex={startPage}
    />
  )
}
